import { config, BASE_DOMAIN } from "./config";

const apiOrigin = config.URL_API;
const publicOrigin = config.URL_PUBLIC;

// Primeros segmentos reservados que NO son slugs de tienda (paneles cross-tienda).
export const RESERVED_SLUGS = ['platform', 'www'];

// Con dominio propio el slug no esta en el hostname: lo resuelve la API.
let resolvedTenant = '';
export const setResolvedTenant = (slug) => { resolvedTenant = slug || ''; };

// Unico lugar donde se decide de que tienda se trata.
export const resolveHost = () => {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return { kind: 'localhost' };
  }
  if (hostname === BASE_DOMAIN) {
    return { kind: 'platform' };
  }
  if (hostname.endsWith(`.${BASE_DOMAIN}`)) {
    const subdomain = hostname.slice(0, -(BASE_DOMAIN.length + 1));
    if (RESERVED_SLUGS.includes(subdomain)) return { kind: 'platform' };
    return { kind: 'subdomain', slug: subdomain };
  }
  return { kind: 'custom', hostname };
};

const getTenantId = () => {
  const host = resolveHost();

  if (host.kind === 'subdomain') return host.slug;
  // Dominio propio: vacio hasta que la API resuelva el slug.
  if (host.kind === 'custom') return resolvedTenant;
  if (host.kind === 'platform') return '';

  // Localhost: slug viene del path (/techdrop/admin → "techdrop")
  const first = window.location.pathname.split('/').filter(Boolean)[0] || '';
  return RESERVED_SLUGS.includes(first) ? '' : first;
};

// Opciones para endpoints platform: Bearer del platformToken, sin X-Tenant-Id.
const platformOptions = (method, data) => {
  const token = localStorage.getItem('platformToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return {
    method,
    headers,
    ...(data && { body: JSON.stringify(data) }),
  };
};

export const publicOptions = (method, data) => ({
  method,
  headers: {
    'Content-Type': 'application/json',
    'X-Tenant-Id': getTenantId(),
  },
  ...(data && { body: JSON.stringify(data) })
});

export const privateOptions = (method, data) => {
  const token = localStorage.getItem("token");
  const currentTenantId = getTenantId();
  const headers = {
    'Content-Type': 'application/json',
    'X-Tenant-Id': currentTenantId,
  };
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // Solo enviar el token si corresponde al tenant actual
      if (!payload.tenantId || payload.tenantId === currentTenantId) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch {
      // Token malformado — no enviarlo
    }
  }
  return {
    method,
    headers,
    ...(data && { body: JSON.stringify(data) })
  };
};

// Wrapper para endpoints privados que requieren autenticación y rol admin.
// Ante 401/403 limpia el token y redirige al login de la tienda.
const privateRequest = async (method, path, data) => {
  const res = await fetch(`${apiOrigin}${path}`, privateOptions(method, data));
  if (res.status === 401 || res.status === 403) {
    localStorage.removeItem('token');
    const tenantId = getTenantId();
    window.location.href = tenantId ? `/${tenantId}/login` : '/';
    return { status: 'error', message: 'Sesión expirada o sin permisos' };
  }
  return res.json();
};

export const api = {
  // Provincias y localidades: públicas, de nuestras tablas (Georef).
  geo: {
    async provincias() {
      const res = await fetch(`${apiOrigin}/geo/provincias`, privateOptions('GET'));
      return await res.json();
    },
    async localidades(idProvincia, q = '') {
      const params = new URLSearchParams({ provincia: idProvincia, q });
      const res = await fetch(`${apiOrigin}/geo/localidades?${params}`, privateOptions('GET'));
      return await res.json();
    },
  },
  tienda: {
    async getConfig(slug) {
      const res = await fetch(`${publicOrigin}/tienda/${slug}`, publicOptions('GET'));
      return await res.json();
    },
    async getConfigByDomain(domain) {
      const res = await fetch(`${publicOrigin}/tienda-by-domain?domain=${encodeURIComponent(domain)}`, publicOptions('GET'));
      return await res.json();
    },
    async updateConfig(data) {
      const res = await fetch(`${apiOrigin}/admin/tienda`, privateOptions('PUT', data));
      return await res.json();
    },
    async uploadImagen(file) {
      const token = localStorage.getItem("token");
      const tenantId = getTenantId();
      const headers = { 'X-Tenant-Id': tenantId };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const form = new FormData();
      form.append('imagen', file);
      const res = await fetch(`${apiOrigin}/admin/upload`, { method: 'POST', headers, body: form });
      return await res.json();
    },
  },
  auth: {
    async sendCode(data) {
      const res = await fetch(`${publicOrigin}/auth/send-code`, publicOptions('POST', data));
      return await res.json();
    },
    async verifyCode(data) {
      const res = await fetch(`${publicOrigin}/auth/verify-code`, publicOptions('POST', data));
      return await res.json();
    },

  },
  platform: {
    async sendCode(data) {
      const res = await fetch(`${publicOrigin}/auth/platform/send-code`, publicOptions('POST', data));
      return await res.json();
    },
    async verifyCode(data) {
      const res = await fetch(`${publicOrigin}/auth/platform/verify-code`, publicOptions('POST', data));
      return await res.json();
    },
    async me() {
      const res = await fetch(`${apiOrigin}/platform/me`, platformOptions('GET'));
      return await res.json();
    },
    tiendas: {
      async list() {
        const res = await fetch(`${apiOrigin}/platform/tiendas`, platformOptions('GET'));
        return await res.json();
      },
      async get(slug) {
        const res = await fetch(`${apiOrigin}/platform/tiendas/${slug}`, platformOptions('GET'));
        return await res.json();
      },
      async create(data) {
        const res = await fetch(`${apiOrigin}/platform/tiendas`, platformOptions('POST', data));
        return await res.json();
      },
      async update(slug, data) {
        const res = await fetch(`${apiOrigin}/platform/tiendas/${slug}`, platformOptions('PUT', data));
        return await res.json();
      },
      async listAdmins(slug) {
        const res = await fetch(`${apiOrigin}/platform/tiendas/${slug}/admins`, platformOptions('GET'));
        return await res.json();
      },
      async addAdmin(slug, data) {
        const res = await fetch(`${apiOrigin}/platform/tiendas/${slug}/admins`, platformOptions('POST', data));
        return await res.json();
      },
      async pasarelas(slug) {
        const res = await fetch(`${apiOrigin}/platform/tiendas/${slug}/pasarelas`, platformOptions('GET'));
        return await res.json();
      },
      async crearPasarela(slug, codigo) {
        const res = await fetch(`${apiOrigin}/platform/tiendas/${slug}/pasarelas`, platformOptions('POST', { codigo }));
        return await res.json();
      },
      async actualizarPasarela(slug, id, data) {
        const res = await fetch(`${apiOrigin}/platform/tiendas/${slug}/pasarelas/${id}`, platformOptions('PUT', data));
        return await res.json();
      },
    },
    geo: {
      async provincias() {
        const res = await fetch(`${apiOrigin}/platform/provincias`, platformOptions('GET'));
        return await res.json();
      },
      async localidades(params = {}) {
        const res = await fetch(`${apiOrigin}/platform/localidades?${new URLSearchParams(params)}`, platformOptions('GET'));
        return await res.json();
      },
      async crearLocalidad(data) {
        const res = await fetch(`${apiOrigin}/platform/localidades`, platformOptions('POST', data));
        return await res.json();
      },
      async editarLocalidad(id, data) {
        const res = await fetch(`${apiOrigin}/platform/localidades/${id}`, platformOptions('PUT', data));
        return await res.json();
      },
      async escritas() {
        const res = await fetch(`${apiOrigin}/platform/localidades/escritas`, platformOptions('GET'));
        return await res.json();
      },
      async resolverEscrita(data) {
        const res = await fetch(`${apiOrigin}/platform/localidades/escritas/resolver`, platformOptions('POST', data));
        return await res.json();
      },
    },
  },
  usuario: {
    async getCurrentUser() {
      const res = await fetch(`${apiOrigin}/usuario/current`, privateOptions('GET'));
      return await res.json();
    },
    async editUsuario(data) {
      const response = await fetch(apiOrigin + '/usuario', privateOptions('PUT', data));
      const res = await response.json();
      return res
    },
    async selectCliente(data) {
      const res = await fetch(`${apiOrigin}/usuario/select-cliente`, privateOptions('POST', data));
      return await res.json();
    },
    async bajaUser() {
      const response = await fetch(apiOrigin + '/usuario/baja', privateOptions('POST'));
      const res = await response.json();
      return res;
    },
  },
  productos: {
    async getAll(q = '') {
      const res = await fetch(`${apiOrigin}/productos?${q}`, privateOptions('GET'));
      return await res.json();
    },
  },
  catalogo: {
    async getProductos(params = {}) {
      const p = typeof params === 'string' ? (params ? { q: params } : {}) : params;
      const qs = new URLSearchParams();
      if (p.q) qs.set('q', p.q);
      if (Array.isArray(p.categoria_id) && p.categoria_id.length > 0) qs.set('categoria_id', p.categoria_id.join(','));
      else if (p.categoria_id) qs.set('categoria_id', p.categoria_id);
      if (p.page) qs.set('page', p.page);
      if (p.limit) qs.set('limit', p.limit);
      const str = qs.toString();
      const res = await fetch(`${apiOrigin}/catalogo/productos${str ? '?' + str : ''}`, privateOptions('GET'));
      return await res.json();
    },
    async getCategorias() {
      const res = await fetch(`${apiOrigin}/catalogo/categorias`, privateOptions('GET'));
      return await res.json();
    },
    async getProducto(id) {
      const res = await fetch(`${apiOrigin}/catalogo/producto/${id}`, privateOptions('GET'));
      return await res.json();
    },
    async getVariantesProducto(id) {
      const res = await fetch(`${apiOrigin}/catalogo/producto/${id}/variantes`, privateOptions('GET'));
      return await res.json();
    },
    async getDestacados() {
      const res = await fetch(`${apiOrigin}/catalogo/productos/destacados`, privateOptions('GET'));
      return await res.json();
    },
  },
  rubros: {
    async getAll(q = "") {
      const res = await fetch(`${apiOrigin}/rubros?${q}`, privateOptions('GET'));
      return await res.json();
    }
  },
  marcas: {
    async getAll(q = "") {
      const res = await fetch(`${apiOrigin}/marcas?${q}`, privateOptions('GET'));
      return await res.json();
    }
  },
  modelos: {
    async getAll() {
      const res = await fetch(`${apiOrigin}/modelos`, privateOptions('GET'));
      return await res.json();
    }
  },
  carrito: {
    async addItem(data) {
      const res = await fetch(`${apiOrigin}/carrito`, privateOptions('POST', data));
      return await res.json();
    },
    async deleteItem(id) {
      const res = await fetch(`${apiOrigin}/carrito/item/${id}`, privateOptions('DELETE'));
      return await res.json();
    },
    async get(id) {
      const res = await fetch(`${apiOrigin}/carrito/${id}`, privateOptions('GET'));
      return await res.json();
    },
    async count() {
      const res = await fetch(`${apiOrigin}/carrito/count`, privateOptions('GET'));
      return await res.json();
    },
  },
  clientes: {
    async mio() {
      return privateRequest('GET', '/mi-cliente');
    },
    async actualizar(data) {
      return privateRequest('PUT', '/mi-cliente', data);
    },
    async misUsuarios() {
      return privateRequest('GET', '/mi-cliente/usuarios');
    },
    async agregarUsuario(mail) {
      return privateRequest('POST', '/mi-cliente/usuarios', { mail });
    },
    async quitarUsuario(uid) {
      return privateRequest('DELETE', `/mi-cliente/usuarios/${uid}`);
    },
    async misDomicilios() {
      return privateRequest('GET', '/mis-domicilios');
    },
    async crearDomicilio(data) {
      return privateRequest('POST', '/mis-domicilios', data);
    },
    async editarDomicilio(did, data) {
      return privateRequest('PUT', `/mis-domicilios/${did}`, data);
    },
    async quitarDomicilio(did) {
      return privateRequest('DELETE', `/mis-domicilios/${did}`);
    },
    async registrar(data) {
      const res = await fetch(`${apiOrigin}/cliente/registrar`, privateOptions('POST', data));
      return await res.json();
    },
  },
  ventas_pedidos: {
    async reservar(carritoId) {
      const res = await fetch(`${apiOrigin}/carrito/${carritoId}/reservar`, privateOptions('POST', {}));
      return await res.json();
    },
    async create(id, data) {
      const res = await fetch(`${apiOrigin}/carrito/${id}/venta_pedido`, privateOptions('POST', data));
      return await res.json();
    },
  },
  pedidos: {
    async getMisPedidos() {
      const res = await fetch(`${apiOrigin}/mis_pedidos`, privateOptions('GET'));
      return await res.json();
    },
    async getDetalle(idCarrito) {
      const res = await fetch(`${apiOrigin}/mis_pedidos/${idCarrito}`, privateOptions('GET'));
      return await res.json();
    },
  },
  pagos: {
    async checkout(pedidoId) {
      const res = await fetch(`${apiOrigin}/pedido/${pedidoId}/checkout`, privateOptions('POST'));
      return await res.json();
    },
    async checkoutPaypal(pedidoId) {
      const res = await fetch(`${apiOrigin}/pedido/${pedidoId}/checkout-paypal`, privateOptions('POST'));
      return await res.json();
    },
    async capturarPaypal(pedidoId, orderId) {
      const res = await fetch(`${apiOrigin}/pedido/${pedidoId}/paypal/capturar`, privateOptions('POST', { order_id: orderId }));
      return await res.json();
    },
    async procesarPago(pedidoId, formData) {
      const res = await fetch(`${apiOrigin}/pedido/${pedidoId}/procesar-pago`, privateOptions('POST', formData));
      return await res.json();
    },
    async simularPago(pedidoId) {
      const res = await fetch(`${apiOrigin}/pedido/${pedidoId}/simular-pago`, privateOptions('POST'));
      return await res.json();
    },
    async getMetodos() {
      const res = await fetch(`${apiOrigin}/payment-methods`, privateOptions('GET'));
      return await res.json();
    },
    async getEstados() {
      const res = await fetch(`${apiOrigin}/payment-estados`, privateOptions('GET'));
      return await res.json();
    },
    async iniciarTransferencia(pedidoId) {
      const res = await fetch(`${apiOrigin}/pedido/${pedidoId}/checkout-transferencia`, privateOptions('POST'));
      return await res.json();
    },
    async subirAdjunto(pedidoId, archivo) {
      const token = localStorage.getItem('token');
      const tenantId = getTenantId();
      const formData = new FormData();
      formData.append('archivo', archivo);
      const res = await fetch(`${apiOrigin}/pedido/${pedidoId}/adjunto`, {
        method: 'POST',
        headers: {
          'X-Tenant-Id': tenantId,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });
      return await res.json();
    },
    async getAdjuntos(pedidoId) {
      const res = await fetch(`${apiOrigin}/pedido/${pedidoId}/adjuntos`, privateOptions('GET'));
      return await res.json();
    },
    async confirmarAdjunto(pedidoId, data) {
      const res = await fetch(`${apiOrigin}/pedido/${pedidoId}/confirmar-adjunto`, privateOptions('POST', data));
      return await res.json();
    },
    async getAdjuntoBlob(adjuntoId) {
      const res = await fetch(`${apiOrigin}/adjunto/${adjuntoId}/file`, privateOptions('GET'));
      if (!res.ok) return null;
      return URL.createObjectURL(await res.blob());
    },
  },
  admin: {
    precios: {
      async getMargenes() {
        return privateRequest('GET', '/admin/precios/margenes');
      },
      async setMargenes(margenes) {
        return privateRequest('PUT', '/admin/precios/margenes', { margenes });
      },
    },
    async clientes(q = '') {
      return privateRequest('GET', `/admin/clientes${q ? `?q=${encodeURIComponent(q)}` : ''}`);
    },
    async cliente(id) {
      return privateRequest('GET', `/admin/clientes/${id}`);
    },
    async crearCliente(data) {
      return privateRequest('POST', '/admin/clientes', data);
    },
    async editarCliente(id, data) {
      return privateRequest('PUT', `/admin/clientes/${id}`, data);
    },
    async setListaCliente(id, idpreciolista) {
      return privateRequest('PUT', `/admin/clientes/${id}/lista`, { idpreciolista });
    },
    async agregarUsuarioCliente(id, mail) {
      return privateRequest('POST', `/admin/clientes/${id}/usuarios`, { mail });
    },
    async quitarUsuarioCliente(id, uid) {
      return privateRequest('DELETE', `/admin/clientes/${id}/usuarios/${uid}`);
    },
    async crearDomicilioCliente(id, data) {
      return privateRequest('POST', `/admin/clientes/${id}/domicilios`, data);
    },
    async editarDomicilioCliente(id, did, data) {
      return privateRequest('PUT', `/admin/clientes/${id}/domicilios/${did}`, data);
    },
    async quitarDomicilioCliente(id, did) {
      return privateRequest('DELETE', `/admin/clientes/${id}/domicilios/${did}`);
    },
    async listasVenta() {
      return privateRequest('GET', '/admin/catalogo/tipos-precio?tipo=venta');
    },
    async productos(q = '') {
      return privateRequest('GET', `/admin/catalogo/productos?${new URLSearchParams({ q, page: 1, limit: 50 })}`);
    },
    async crearPedido(data) {
      return privateRequest('POST', '/admin/pedidos', data);
    },
    async emailLogs(params = {}) {
      return privateRequest('GET', `/admin/email-logs?${new URLSearchParams(params)}`);
    },
    async emailLog(id) {
      return privateRequest('GET', `/admin/email-logs/${id}`);
    },
    async reenviarEmail(id) {
      return privateRequest('POST', `/admin/email-logs/${id}/reenviar`);
    },
    async listAdmins() {
      return privateRequest('GET', '/admin/admins');
    },
    async addAdmin(data) {
      return privateRequest('POST', '/admin/admins', data);
    },
    async deleteAdmin(id) {
      return privateRequest('DELETE', `/admin/admins/${id}`);
    },
    async getDashboard(signal) {
      const res = await fetch(`${apiOrigin}/admin/dashboard`, { ...privateOptions('GET'), signal });
      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem('token');
        const tenantId = getTenantId();
        window.location.href = tenantId ? `/${tenantId}/login` : '/';
        return { status: 'error', message: 'Sesión expirada o sin permisos' };
      }
      return res.json();
    },
    async getPedidos() {
      return privateRequest('GET', '/admin/pedidos');
    },
    async getPedidoEstados() {
      return privateRequest('GET', '/admin/pedido-estados');
    },
    async getPedidoDetalle(id) {
      return privateRequest('GET', `/admin/pedidos/${id}`);
    },
    async getHistorialPedido(id) {
      return privateRequest('GET', `/admin/pedidos/${id}/historial`);
    },
    async getCobros(estado = '', cliente = '', desde = '', hasta = '', metodo = '') {
      const params = [];
      if (estado) params.push(`estado=${encodeURIComponent(estado)}`);
      if (cliente) params.push(`cliente=${encodeURIComponent(cliente)}`);
      if (desde) params.push(`desde=${desde}`);
      if (hasta) params.push(`hasta=${hasta}`);
      if (metodo) params.push(`metodo=${encodeURIComponent(metodo)}`);
      const q = params.length ? `?${params.join('&')}` : '';
      return privateRequest('GET', `/admin/cobros${q}`);
    },
    async aprobarPago(pedidoId, idMetodoPago = null) {
      return privateRequest('POST', `/admin/cobros/${pedidoId}/aprobar`, { id_metodo_pago: idMetodoPago });
    },
    async rechazarPago(pedidoId, motivo = '') {
      return privateRequest('POST', `/admin/cobros/${pedidoId}/rechazar`, { motivo });
    },
    catalogo: {
      async getProductos({ q = '', page = 1, limit = 25 } = {}) {
        return privateRequest('GET', `/admin/catalogo/productos?${new URLSearchParams({ q, page, limit })}`);
      },
      async getProducto(id) {
        return privateRequest('GET', `/admin/catalogo/productos/${id}`);
      },
      async crearProducto(data) {
        return privateRequest('POST', '/admin/catalogo/productos', data);
      },
      async editarProducto(id, data) {
        return privateRequest('PUT', `/admin/catalogo/productos/${id}`, data);
      },
      async eliminarProducto(id) {
        return privateRequest('DELETE', `/admin/catalogo/productos/${id}`);
      },
      async agregarPrecio(id, data) {
        return privateRequest('POST', `/admin/catalogo/productos/${id}/precios`, data);
      },
      async getTiposPrecio() {
        return privateRequest('GET', '/admin/catalogo/tipos-precio');
      },
      async getCategorias() {
        return privateRequest('GET', '/admin/catalogo/categorias');
      },
      async crearCategoria(data) {
        return privateRequest('POST', '/admin/catalogo/categorias', data);
      },
      async editarCategoria(id, data) {
        return privateRequest('PUT', `/admin/catalogo/categorias/${id}`, data);
      },
      async eliminarCategoria(id) {
        return privateRequest('DELETE', `/admin/catalogo/categorias/${id}`);
      },
      async setProductoCategorias(productoId, ids) {
        return privateRequest('PUT', `/admin/catalogo/productos/${productoId}/categorias`, { ids });
      },
      async setProductoAsociados(productoId, asociadoIds) {
        return privateRequest('PUT', `/admin/catalogo/productos/${productoId}/asociados`, { asociado_ids: asociadoIds });
      },
      async uploadProductoImagen(file) {
        const token = localStorage.getItem('token');
        const tenantId = getTenantId();
        const headers = { 'X-Tenant-Id': tenantId };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const form = new FormData();
        form.append('imagen', file);
        const res = await fetch(`${apiOrigin}/admin/catalogo/productos/upload-imagen`, { method: 'POST', headers, body: form });
        return await res.json();
      },
      async getProductoAdjuntos(id) {
        return privateRequest('GET', `/admin/catalogo/productos/${id}/adjuntos`);
      },
      async addProductoAdjunto(id, url) {
        return privateRequest('POST', `/admin/catalogo/productos/${id}/adjuntos`, { url });
      },
      async uploadProductoAdjunto(id, file) {
        const token = localStorage.getItem('token');
        const tenantId = getTenantId();
        const headers = { 'X-Tenant-Id': tenantId };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const form = new FormData();
        form.append('imagen', file);
        const res = await fetch(`${apiOrigin}/admin/catalogo/productos/${id}/adjuntos/upload`, { method: 'POST', headers, body: form });
        return await res.json();
      },
      async deleteProductoAdjunto(adjuntoId) {
        return privateRequest('DELETE', `/admin/catalogo/adjuntos/${adjuntoId}`);
      },
      async getPrecioAuto(id) {
        return privateRequest('GET', `/admin/catalogo/productos/${id}/precio-auto`);
      },
      async setPrecioAuto(id, data) {
        return privateRequest('PUT', `/admin/catalogo/productos/${id}/precio-auto`, data);
      },
      async descripcionIA(nombre, categorias) {
        return privateRequest('POST', '/admin/catalogo/descripcion-ia', { nombre, categorias });
      },
      async serpapiSearch(q) {
        return privateRequest('GET', `/admin/catalogo/serpapi-search?q=${encodeURIComponent(q)}`);
      },
      descargarPlantilla() {
        const token = localStorage.getItem('token');
        const tenantId = getTenantId();
        const headers = { 'X-Tenant-Id': tenantId };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        return fetch(`${apiOrigin}/admin/catalogo/productos/plantilla`, { headers });
      },
      async previewImportacion(file) {
        const token = localStorage.getItem('token');
        const tenantId = getTenantId();
        const headers = { 'X-Tenant-Id': tenantId };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const form = new FormData();
        form.append('archivo', file);
        const res = await fetch(`${apiOrigin}/admin/catalogo/productos/importar/preview`, { method: 'POST', headers, body: form });
        return await res.json();
      },
      async importarProductos(file) {
        const token = localStorage.getItem('token');
        const tenantId = getTenantId();
        const headers = { 'X-Tenant-Id': tenantId };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const form = new FormData();
        form.append('archivo', file);
        const res = await fetch(`${apiOrigin}/admin/catalogo/productos/importar`, { method: 'POST', headers, body: form });
        return await res.json();
      },
      async importarEstado(jobId) {
        return privateRequest('GET', `/admin/catalogo/productos/importar/status/${jobId}`);
      },
      descargarPlantillaCategorias() {
        const token = localStorage.getItem('token');
        const tenantId = getTenantId();
        const headers = { 'X-Tenant-Id': tenantId };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        return fetch(`${apiOrigin}/admin/catalogo/categorias/plantilla`, { headers });
      },
      async previewImportacionCategorias(file) {
        const token = localStorage.getItem('token');
        const tenantId = getTenantId();
        const headers = { 'X-Tenant-Id': tenantId };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const form = new FormData();
        form.append('archivo', file);
        const res = await fetch(`${apiOrigin}/admin/catalogo/categorias/importar/preview`, { method: 'POST', headers, body: form });
        return await res.json();
      },
      async importarCategorias(file) {
        const token = localStorage.getItem('token');
        const tenantId = getTenantId();
        const headers = { 'X-Tenant-Id': tenantId };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const form = new FormData();
        form.append('archivo', file);
        const res = await fetch(`${apiOrigin}/admin/catalogo/categorias/importar`, { method: 'POST', headers, body: form });
        return await res.json();
      },
      // Propiedades
      async getPropiedades() {
        return privateRequest('GET', '/admin/catalogo/propiedades');
      },
      async crearPropiedad(data) {
        return privateRequest('POST', '/admin/catalogo/propiedades', data);
      },
      async eliminarPropiedad(id) {
        return privateRequest('DELETE', `/admin/catalogo/propiedades/${id}`);
      },
      async agregarValorPropiedad(propId, data) {
        return privateRequest('POST', `/admin/catalogo/propiedades/${propId}/valores`, data);
      },
      async eliminarValorPropiedad(id) {
        return privateRequest('DELETE', `/admin/catalogo/propiedades/valores/${id}`);
      },
      // Variantes
      async getVariantes(productoId) {
        return privateRequest('GET', `/admin/catalogo/productos/${productoId}/variantes`);
      },
      async crearVariantes(productoId, variantes) {
        return privateRequest('POST', `/admin/catalogo/productos/${productoId}/variantes`, { variantes });
      },
      async eliminarTodasVariantes(productoId) {
        return privateRequest('DELETE', `/admin/catalogo/productos/${productoId}/variantes`);
      },
      async editarVariante(id, data) {
        return privateRequest('PUT', `/admin/catalogo/variantes/${id}`, data);
      },
      async eliminarVariante(id) {
        return privateRequest('DELETE', `/admin/catalogo/variantes/${id}`);
      },
    },
    async tesoreros() {
      return privateRequest('GET', '/admin/cuentas-transferencia/tesoreros');
    },
    async corregirCuentaPago(pedidoId, idCuenta) {
      return privateRequest('PUT', `/admin/cobros/${pedidoId}/cuenta`, { id_cuenta: idCuenta });
    },
    async reporteTransferencias(desde, hasta) {
      return privateRequest('GET', `/admin/reportes/transferencias?${new URLSearchParams({ desde, hasta })}`);
    },
    async cambiarRolColaborador(id, rol) {
      return privateRequest('PUT', `/admin/admins/${id}/rol`, { rol });
    },
    async getCuentasTransferencia() {
      const res = await fetch(`${apiOrigin}/admin/cuentas-transferencia`, privateOptions('GET'));
      return await res.json();
    },
    async crearCuenta(data) {
      const res = await fetch(`${apiOrigin}/admin/cuentas-transferencia`, privateOptions('POST', data));
      return await res.json();
    },
    async editarCuenta(id, data) {
      const res = await fetch(`${apiOrigin}/admin/cuentas-transferencia/${id}`, privateOptions('PUT', data));
      return await res.json();
    },
    async toggleCuenta(id) {
      const res = await fetch(`${apiOrigin}/admin/cuentas-transferencia/${id}/toggle`, privateOptions('PUT'));
      return await res.json();
    },
    async eliminarCuenta(id) {
      const res = await fetch(`${apiOrigin}/admin/cuentas-transferencia/${id}`, privateOptions('DELETE'));
      return await res.json();
    },
  },
  promociones: {
    async simular(subtotal, items = []) {
      return privateRequest('POST', '/promociones/simular', { subtotal, items });
    },
    admin: {
      async getTipos() {
        return privateRequest('GET', '/admin/promociones/tipos');
      },
      async getAll() {
        return privateRequest('GET', '/admin/promociones');
      },
      async crear(data) {
        return privateRequest('POST', '/admin/promociones', data);
      },
      async editar(id, data) {
        return privateRequest('PUT', `/admin/promociones/${id}`, data);
      },
      async toggle(id) {
        return privateRequest('PUT', `/admin/promociones/${id}/toggle`);
      },
      async eliminar(id) {
        return privateRequest('DELETE', `/admin/promociones/${id}`);
      },
    },
  },
  faq: {
    async getFaqs() {
      const res = await fetch(`${apiOrigin}/faqs`, privateOptions('GET'));
      return await res.json();
    },
    admin: {
      async getAll() {
        return privateRequest('GET', '/admin/faqs');
      },
      async crear(data) {
        return privateRequest('POST', '/admin/faqs', data);
      },
      async editar(id, data) {
        return privateRequest('PUT', `/admin/faqs/${id}`, data);
      },
      async ordenar(ids) {
        return privateRequest('PUT', '/admin/faqs/orden', { ids });
      },
      async eliminar(id) {
        return privateRequest('DELETE', `/admin/faqs/${id}`);
      },
    },
  },
};
